import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { PlusCircle, Save2, Trash, Calendar3, Receipt, PersonFill, CardList, Coin, Copy } from 'react-bootstrap-icons'
import { Container, Row, Col } from 'react-bootstrap'
import SearchSelect from '../components/form/SearchSelect'
import InputNumber from '../components/form/InputNumber'
import InputDate from '../components/form/InputDate'
import InvoicesService from '../services/InvoicesService'
import CustomersService from '../services/CustomersService'
import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"

const InvoicesForm = (props) => {

    const intl = useIntl()
    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [invoiceNumber, setInvoiceNumber] = useState('')
    const [invoiceNumberErrorKey, setInvoiceNumberErrorKey] = useState('')
    const [date, setDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [concept, setConcept] = useState('')
    const [customerId, setCustomerId] = useState('')
    const [customersList, setCustomersList] = useState([])
    const [customerErrorKey, setCustomerErrorKey] = useState('')
    const [amount, setAmount] = useState()
    const [estimatedDate, setEstimatedDate] = useState(formatDate(Date.now(), 'YYYY-MM-DD'))
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    // Fetch customers
    const fetchCustomers = async () => {
        try {
            const response = await CustomersService.getCustomers(1, '', 2000)
            setCustomersList(response.data.customers)
        } catch (err) {
            setError(err)
        }
    }

    const getData = async () => {
        await InvoicesService.getInvoice(elementId).then(
            response => {
                setInvoiceNumber(response.data.invoiceNumber || '')
                setDate(formatDate(response.data.date, 'YYYY-MM-DD'))
                setConcept(response.data.concept || '')
                setCustomerId(response.data.customerId || '')
                setAmount(response.data.amount || 0)
                setEstimatedDate(formatDate(response.data.estimatedDate, 'YYYY-MM-DD'))
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {
        fetchCustomers()

        if (elementId) {
            getData()
        }

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            case 'duplicate':
                setButtonTitle(<FormattedMessage id="textDuplicate" defaultMessage="Duplicate" />)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        if (name === 'customerId') {
            setCustomerErrorKey('')
        }
        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'invoiceNumber':
                setInvoiceNumber(value)
                break
            case 'date':
                setDate(value)
                break
            case 'concept':
                setConcept(value)
                break
            case 'customerId':
                setCustomerId(value)
                break
            case 'amount':
                setAmount(value)
                break
            case 'estimatedDate':
                setEstimatedDate(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        setCustomerErrorKey('')
        setInvoiceNumberErrorKey('')

        const form = e.currentTarget
        let finalCustomerId = customerId

        const matchedCustomer = customersList.find(c => 
            c.name.toLowerCase() === (customerId?.toString().toLowerCase() || '') || 
            c._id === customerId
        );

        if (matchedCustomer) {
            finalCustomerId = matchedCustomer._id
        }

        const hasValue = customerId && customerId.toString().trim() !== ''
        const isInvalidCustomer = hasValue && !/^[0-9a-fA-F]{24}$/.test(finalCustomerId)
        const isFormInvalid = !form.checkValidity()
        const isEmpty = !hasValue

        if (isFormInvalid || isInvalidCustomer || isEmpty) {
            setFormValid(true)
            if (isEmpty) {
                setCustomerErrorKey('invoicesCustomerInvalid') 
            } else if (isInvalidCustomer) {
                setCustomerErrorKey('invoiceCustomerNotFound')
            }
            return
        }

        const invoice = {
            invoiceNumber,
            date,
            concept,
            customerId: finalCustomerId || null,
            amount,
            estimatedDate
        }

        try {
            if (elementId && action === "edit") {
                await InvoicesService.updateInvoice(invoice, elementId)
            } else if (action === "add" || action === "duplicate") {
                await InvoicesService.addInvoice(invoice)
            } else if (action === "delete") {
                await InvoicesService.deleteInvoice(elementId)
            }

            setFormValid(false)
            onSubmit()
        } catch (error) {
            const serverError = error.response?.data;
            const msg = serverError?.error?.message || "";

            
            if (msg.includes("Cast to ObjectId failed")) {
                setCustomerErrorKey('invoiceCustomerNotFound');
                setFormValid(true);
            } 
            
            else if (serverError?.errorKey === "invoicesNumberAlreadyExists") {
                setInvoiceNumberErrorKey('invoicesNumberAlreadyExists')
                setFormValid(true); 
            } 
            
            else {
                setShowMsg({ text: serverError?.msg || "An error occurred", style: 'danger' });
                setShowAlert(true);
            }
        }
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}
                <Row className="mt-4">
                    <Col md={5}>
                        <Form.Group controlId="date">
                            <Calendar3 /><Form.Label className="fw-semibold mb-1 ms-2"><FormattedMessage id="invoicesDate" defaultMessage="Invoice date" />:</Form.Label>
                            <InputDate
                                fieldName='date'
                                value={date}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="invoicesDateInvalid" defaultMessage="Provide an invoice date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    <Col md={1}></Col>


                    <Col md={6}>
                        <Form.Group controlId="invoiceNumber">
                            <Receipt /><Form.Label className="fw-semibold mb-1 ms-2"><FormattedMessage id="invoicesNumber" defaultMessage="Invoice number" />:</Form.Label>
                            <Form.Control
                                type="text"
                                name="invoiceNumber"
                                value={invoiceNumber}
                                onChange={(e) => {
                                    setInvoiceNumberErrorKey('')
                                    handleInputChange(e)
                                }}
                                disabled={inputDisabled}
                                required="required"
                                className={invoiceNumberErrorKey !== '' ? 'is-invalid' : ''}
                                style={
                                    invoiceNumberErrorKey !== '' 
                                    ? { borderColor: '#dc3545', backgroundImage: 'none' } 
                                    : {}
                                }
                            />

                            {invoiceNumberErrorKey !== '' ? (
                                <div className="text-danger small mt-1">
                                    <FormattedMessage id={invoiceNumberErrorKey} />
                                </div>
                            ) : (
                                <Form.Control.Feedback type="invalid">
                                    <FormattedMessage id="invoicesNumberInvalid" defaultMessage="Provide an invoice number" />
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mt-4" controlId="customerId">
                    <PersonFill /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="invoicesCustomer" defaultMessage="Customer" />:</Form.Label>

                    <SearchSelect
                        fieldName={'customerId'}
                        selected={customerId}
                        onChange={handleInputChange}
                        options={customersList}
                        disabled={customersList.length === 0}
                        className={customerErrorKey !== '' ? 'is-invalid border-danger' : (formValid && customerId ? 'is-valid' : '')}
                        style={customerErrorKey !== '' ? { backgroundImage: 'none' } : {}}
                        placeholder={customersList.length === 0 ? intl.formatMessage({ id: "invoiceCostumersNotFound", defaultMessage: "No costumers available" }) : intl.formatMessage({ id: "textClickSelect", defaultMessage: "Click to select or search" })}
                    />

                    {customerErrorKey !== '' && (
                        <div className="text-danger small mt-1" style={{ display: 'block' }}>
                            <FormattedMessage id={customerErrorKey} />
                        </div>
                    )}

                </Form.Group>

                <Form.Group className="mt-4" controlId="concept">
                    <CardList /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="invoicesConcept" defaultMessage="Concept" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="concept"
                        value={concept}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="invoicesConceptInvalid" defaultMessage="Provide a concept" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Row className="mt-4">
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="amount">
                            <Coin /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="invoicesAmount" defaultMessage="Amount" />:</Form.Label>
                            <InputNumber
                                fieldName={"amount"}
                                value={amount}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required="required"
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="invoicesAmountInvalid" defaultMessage="Provide an amount" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="estimatedDate">
                            <Calendar3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="invoicesEstimatedDate" defaultMessage="Estimated date" />:</Form.Label>
                            <InputDate
                                fieldName='estimatedDate'
                                value={estimatedDate}
                                onChange={handleInputChange}
                                disabled={inputDisabled && ("disabled")}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                <FormattedMessage id="invoicesEstimatedDateInvalid" defaultMessage="Provide an estimated income date" />
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2 mt-4">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}
                    
                    {action === "duplicate" && (
                        <Button className="btn btn-dark fw-semibold" type="submit">
                            <Copy /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div >
    )

}

export default InvoicesForm